<!-- ChatWidget.vue -->
<template>
  <div class="body chat-widget" :class="{ 'expanded': isExpanded, 'minimized': isMinimized }">
    <!-- Chat Toggle Button -->
    <button 
      v-if="!isExpanded && !isMinimized"
      class="chat-toggle-btn"
      @click="openChat"
      :title="getDepartmentName(department)"
    >
      <span class="chat-icon">💬</span>
      <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
    </button>

    <!-- Chat Window -->
    <div v-if="isExpanded" class="chat-window">
      <!-- Chat Header -->
      <div class="chat-header">
        <div class="header-left">
          <span class="dept-icon" :class="department">
            {{ department === 'financial' ? '💰' : department === 'commercial' ? '📊' : '👤' }}
          </span>
          <h4>{{ getDepartmentName(department) }}</h4>
          <span v-if="chatStatus" class="status-badge" :class="chatStatus">
            {{ getStatusText(chatStatus) }}
          </span>
        </div>
        <div class="header-right">
          <button class="header-btn" @click="minimizeChat" :title="$t('messenger.chatWidget.minimize')">─</button>
          <button class="header-btn" style="background-color: red;" @click="closeChat" :title="$t('messenger.chatWidget.close')">×</button>
        </div>
      </div>

      <!-- Chat Closed by Operator -->
      <div v-if="chatStatus === 'ended' || chatStatus === 'closed_by_operator'" class="chat-ended-section">
        <div class="ended-icon">✅</div>
        <h4>{{ $t('messenger.chatWidget.chat_ended') }}</h4>
        <p v-if="chatStatus === 'closed_by_operator'">
          {{ $t('messenger.chatWidget.operator_ended_chat') }}
        </p>
        <p v-else>
          {{ $t('messenger.chatWidget.chat_ended_message') }}
        </p>
        <div class="chat-summary" v-if="messages.length > 0">
          <p><strong>{{ $t('messenger.chatWidget.chat_summary') }}:</strong></p>
          <ul class="summary-list">
            <li>{{ $t('messenger.chatWidget.total_messages') }}: {{ messages.length }}</li>
            <li>{{ $t('messenger.chatWidget.started_at') }}: {{ formatTime(questionTimestamp || chatStartTime) }}</li>
            <li v-if="chatEndTime">{{ $t('messenger.chatWidget.ended_at') }}: {{ formatTime(chatEndTime) }}</li>
          </ul>
        </div>
        <button class="restart-btn" @click="resetChat">
          {{ $t('messenger.chatWidget.start_new_chat') }}
        </button>
        <button v-if="messages.length > 0" class="save-chat-btn" @click="saveChatHistory">
          💾 {{ $t('messenger.chatWidget.save_history') }}
        </button>
      </div>

      <!-- Waiting for Operator -->
      <div v-else-if="chatStatus === 'waiting'" class="waiting-section">
        <div class="waiting-icon">⏳</div>
        <h4>{{ $t('messenger.chatWidget.waiting_for_operator') }}</h4>
        <p>{{ $t('messenger.chatWidget.waiting_message') }}</p>
        <div class="waiting-info">
          <div class="info-item">
            <span>{{ $t('messenger.chatWidget.queue_position') }}:</span>
            <strong>{{ queuePosition }}</strong>
          </div>
          <div class="info-item">
            <span>{{ $t('messenger.chatWidget.estimated_wait_time') }}:</span>
            <strong>{{ estimatedWaitTime }} {{ $t('messenger.chatWidget.seconds') }}</strong>
          </div>
        </div>
        <div class="waiting-actions">
          <button class="cancel-btn" @click="cancelQuestion">{{ $t('messenger.chatWidget.cancel_request') }}</button>
          <button class="end-chat-btn" @click="endChatFromCustomer">{{ $t('messenger.chatWidget.end_chat') }}</button>
        </div>
      </div>

      <!-- Chat Messages -->
      <div v-else-if="chatStatus === 'active'" class="chat-messages" ref="messagesContainer">
        <!-- Chat messages -->
        <div 
          v-for="(message, index) in messages" 
          :key="index"
          class="message"
          :class="message.sender"
        >
          <!-- Text message -->
          <div v-if="message.type !== 'file'" class="message-content">
            <span class="message-text">{{ message.text }}</span>
            <span class="message-time">{{ formatTime(message.timestamp) }}</span>
          </div>
          
          <!-- File message -->
          <div v-else class="file-message">
            <div class="file-message-header">
              <span class="file-icon">{{ getFileIcon(message.fileType) }}</span>
              <div class="file-info">
                <p class="file-name">{{ message.fileName }}</p>
                <p class="file-size">{{ formatFileSize(message.fileSize) }}</p>
              </div>
              <button 
                v-if="message.downloadUrl"
                class="download-btn"
                @click="downloadFile(message)"
                :title="$t('messenger.chatWidget.download')"
              >
                ⬇️
              </button>
            </div>
            
            <!-- File caption -->
            <p v-if="message.caption" class="file-caption">
              {{ message.caption }}
            </p>
            
            <!-- File preview for images -->
            <div v-if="isImageFile(message.fileType) && hasPreview(message)" class="file-preview">
              <img 
                :src="getPreviewUrl(message)" 
                :alt="message.fileName"
                @click="openImagePreview(message)"
                class="image-preview"
                loading="lazy"
                @error="handleImageError"
              />
              <div class="image-overlay" @click="openImagePreview(message)">
                <span class="zoom-icon">🔍</span>
              </div>
            </div>
            
            <span class="message-time">{{ formatTime(message.timestamp) }}</span>
          </div>
          
          <div class="message-sender">
            {{ message.sender === 'customer' ? $t('messenger.chatWidget.you') : $t('messenger.chatWidget.operator') }}
          </div>
        </div>
        
        <!-- Operator Info -->
        <div v-if="assignedOperator" class="operator-info">
          <div class="operator-avatar">
            {{ getInitials(assignedOperator.name) }}
          </div>
          <div class="operator-details">
            <strong>{{ assignedOperator.name }}</strong>
            <span>{{ $t('messenger.chatWidget.operator_prefix') }} {{ getDepartmentName(department) }}</span>
          </div>
          <button 
            v-if="chatStatus === 'active'"
            @click="endChatFromCustomer"
            class="end-chat-btn-small"
            :title="$t('messenger.chatWidget.end_chat')"
          >
            {{ $t('messenger.chatWidget.end_chat') }}
          </button>
        </div>
        
        <!-- Empty state -->
        <div v-if="messages.length === 0 && chatStatus === 'active'" class="empty-state">
          <div class="empty-icon">💬</div>
          <p>{{ $t('messenger.chatWidget.empty_chat_message') }}</p>
          <button @click="endChatFromCustomer" class="end-chat-btn-empty">
            {{ $t('messenger.chatWidget.end_chat') }}
          </button>
        </div>
      </div>

      <!-- Initial Question Form -->
      <div v-else-if="!hasAskedQuestion" class="question-form">
        <h4>{{ $t('messenger.chatWidget.ask_question') }}</h4>
        <textarea 
          v-model="questionText"
          :placeholder="$t('messenger.chatWidget.question_placeholder')"
          rows="4"
          class="message-textarea"
          :maxlength="500"
        ></textarea>
        <div class="char-counter">
          {{ questionText.length }}/500
        </div>
        <button 
          @click="submitQuestion"
          :disabled="!questionText.trim() || submitting"
          class="submit-btn"
        >
          {{ submitting ? $t('messenger.chatWidget.sending') : $t('messenger.chatWidget.submit_question') }}
        </button>
      </div>

      <!-- Message Input -->
      <div v-if="chatStatus === 'active'" class="message-input">
        <!-- Add file upload button -->
        <button 
          class="file-upload-btn"
          @click="openFileUploadModal"
          :title="$t('messenger.chatWidget.send_file')"
          :disabled="sendingMessage"
        >
          📎
        </button>
        
        <textarea 
          v-model="messageText"
          :placeholder="$t('messenger.chatWidget.message_placeholder')"
          @keyup.enter.ctrl="sendMessage"
          rows="2"
          class="message-textarea"
        ></textarea>
        
        <div class="input-actions">
          <button 
            @click="sendMessage"
            :disabled="!messageText.trim() || sendingMessage"
            class="send-btn"
          >
            {{ sendingMessage ? '...' : '↵' }}
          </button>
        </div>
      </div>

      <!-- Chat Footer -->
      <div class="chat-footer">
        <small v-if="lastSeen && chatStatus === 'active'">
          {{ $t('messenger.chatWidget.operator_last_seen') }} {{ formatLastSeen(lastSeen) }}
        </small>
        <small v-else>
          {{ $t('messenger.chatWidget.chat_prefix') }} {{ getDepartmentName(department) }}
        </small>
        <small v-if="chatStatus === 'active'" class="end-chat-hint">
          {{ $t('messenger.chatWidget.end_chat_hint') }}
        </small>
      </div>
    </div>

    <!-- Minimized Chat -->
    <div v-if="isMinimized" class="minimized-chat" @click="restoreChat">
      <span class="minimized-icon">💬</span>
      <span class="minimized-title">{{ $t('messenger.chatWidget.chat_prefix') }} {{ getDepartmentName(department) }}</span>
      <span v-if="unreadCount > 0" class="minimized-badge">{{ unreadCount }}</span>
      <button 
        v-if="chatStatus === 'active'"
        @click.stop="endChatFromCustomer"
        class="minimized-end-btn"
        :title="$t('messenger.chatWidget.end_chat')"
      >
        ×
      </button>
    </div>
    
    <FileUploadModal
      v-if="showFileUploadModal"
      :chat-id="currentChatId"
      :sender-id="getCustomerId"
      @file-sent="handleFileSent"
      @close="closeFileUploadModal"
    />

    <div v-if="showImagePreview" class="image-preview-modal" @click="closeImagePreview">
      <div class="preview-content" @click.stop>
        <button class="close-preview-btn" @click="closeImagePreview">×</button>
        <img :src="previewImageUrl" :alt="previewImageName" class="full-image" />
        <div class="image-info">
          <p>{{ previewImageName }}</p>
          <button @click="downloadFile(previewingFile)" class="download-full-btn">
            ⬇️ {{ $t('messenger.chatWidget.download') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useMessenger } from '@/services/messenger'
import { messengerService, API_MESSENGER_URL } from '@/services/apiMessanger'
import FileUploadModal from '@/views/Messanger/FileUploadModal.vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'ChatWidget',
  components: {
    FileUploadModal
  },
  props: {
    department: {
      type: String,
      default: 'financial',
      validator: (value) => ['financial', 'commercial', 'customer', 'technical', 'support'].includes(value)
    },
    companyId: {
      type: [String, Number],
      required: true
    }
  },
  setup(props) {
    const authStore = useAuthStore()
    const messagesContainer = ref(null)
    const { t } = useI18n()

    // Add new state variables
    const showFileUploadModal = ref(false)
    const showImagePreview = ref(false)
    const previewImageUrl = ref('')
    const previewImageName = ref('')
    const previewingFile = ref(null)
    
    // Computed properties for auth store values
    const isAuthenticated = computed(() => authStore.isAuthenticated)
    const getCustomerId = computed(() => authStore.getCustomerId)
    const userPhone = computed(() => authStore.user?.phone || '')
    const userName = computed(() => authStore.user?.name || '')
    
    // File handling methods
    const openFileUploadModal = () => {
      // Check if user is authenticated
      if (!isAuthenticated.value) {
        alert('لطفاً ابتدا وارد سیستم شوید.')
        return
      }
      
      // Check if we have customer ID
      if (!getCustomerId.value) {
        alert('شناسه کاربری نامعتبر است.')
        return
      }
      
      // Check if chat is active
      if (!currentChatId.value || chatStatus.value !== 'active') {
        alert('لطفاً ابتدا یک گفتگو را شروع کنید.')
        return
      }
      
      showFileUploadModal.value = true
    }
    
    const closeFileUploadModal = () => {
      showFileUploadModal.value = false
    }
    
    const getFileIcon = (fileType) => {
      if (!fileType) return '📁'
      if (fileType.startsWith('image/')) return '🖼️'
      if (fileType.includes('pdf')) return '📕'
      if (fileType.includes('document') || fileType.includes('word')) return '📄'
      if (fileType.startsWith('video/')) return '🎬'
      if (fileType.startsWith('audio/')) return '🎵'
      if (fileType.includes('zip') || fileType.includes('rar')) return '📦'
      if (fileType.includes('excel') || fileType.includes('sheet')) return '📊'
      return '📁'
    }
    
    const formatFileSize = (bytes) => {
      if (!bytes) return '0 بایت'
      const k = 1024
      const sizes = ['بایت', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
    
    const isImageFile = (fileType) => {
      return fileType && fileType.startsWith('image/')
    }
    
    const openImagePreview = (fileMessage) => {
      previewImageUrl.value = `${API_MESSENGER_URL}${fileMessage.thumbnailUrl}`
      previewImageName.value = fileMessage.fileName
      previewingFile.value = fileMessage
      showImagePreview.value = true
    }
    
    const closeImagePreview = () => {
      showImagePreview.value = false
      previewImageUrl.value = ''
      previewImageName.value = ''
      previewingFile.value = null
    }

    // Add these missing helper methods
    const getPreviewUrl = (fileMessage) => {
      if (fileMessage.thumbnailUrl) {
        if (fileMessage.thumbnailUrl.startsWith('/')) {
          return `${API_MESSENGER_URL}${fileMessage.thumbnailUrl}`
        }
        return fileMessage.thumbnailUrl
      }
      
      if (fileMessage.downloadUrl && isImageFile(fileMessage.fileType)) {
        if (fileMessage.downloadUrl.startsWith('/')) {
          return `${API_MESSENGER_URL}${fileMessage.downloadUrl}`
        }
        return fileMessage.downloadUrl
      }
      
      if (fileMessage.previewUrl) {
        if (fileMessage.previewUrl.startsWith('/')) {
          return `${API_MESSENGER_URL}${fileMessage.previewUrl}`
        }
        return fileMessage.previewUrl
      }
      
      return null
    }

    const hasPreview = (fileMessage) => {
      return isImageFile(fileMessage.fileType) && 
             (fileMessage.thumbnailUrl || fileMessage.downloadUrl || fileMessage.previewUrl)
    }

    const handleImageError = (event) => {
      console.error('Image failed to load:', event.target.src)
      const img = event.target
      const originalSrc = img.src
      
      if (originalSrc.includes('/thumbnail/')) {
        const fileId = originalSrc.split('/thumbnail/')[1]
        const downloadUrl = `${API_MESSENGER_URL}/chat/file/${fileId}`
        img.src = downloadUrl
      }
      
      img.onerror = () => {
        img.style.display = 'none'
        const previewDiv = img.closest('.file-preview')
        if (previewDiv) {
          previewDiv.style.display = 'none'
        }
      }
    }
    
    const downloadFile = async (fileMessage) => {
      console.log('Downloading file:', fileMessage)
      if (!fileMessage) {
        console.error('No file message provided for download')
        alert('خطا: اطلاعات فایل نامعتبر است.')
        return
      }
      
      try {
        let downloadUrl = fileMessage.downloadUrl || fileMessage.file_url
        
        if (!downloadUrl && fileMessage.id) {
          const response = await messengerService.getFileDownloadUrl(
            fileMessage.file_id || fileMessage.id,
            currentChatId.value
          )
          const data = response.data || response
          
          if (data.status === 'success') {
            downloadUrl = data.download_url
          }
        }
        
        if (downloadUrl) {
          if (downloadUrl.startsWith('/')) {
            const baseUrl = API_MESSENGER_URL
            downloadUrl = `${baseUrl}${downloadUrl}`
          }
          
          console.log('Final download URL:', downloadUrl)
          
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = fileMessage.fileName || fileMessage.filename
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          
          setTimeout(() => {
            document.body.removeChild(link)
            URL.revokeObjectURL(downloadUrl)
          }, 100)
        } else {
          alert('لینک دانلود در دسترس نیست.')
        }
      } catch (error) {
        console.error('Error downloading file:', error)
        alert('خطا در دانلود فایل.')
      }
    }
    
    // Messenger setup
    const { 
      messenger: messengerClient,
      isConnected,
      isLoading: messengerLoading,
      error: messengerError,
      connect: connectMessenger,
      sendMessage: sendMessengerMessage,
      disconnect: disconnectMessenger
    } = useMessenger()

    console.log(isConnected, messengerLoading, messengerError, sendMessengerMessage)
    // State
    const isExpanded = ref(false)
    const isMinimized = ref(false)
    const questionText = ref('')
    const messageText = ref('')
    const messages = ref([])
    const chatStatus = ref(null)
    const initialQuestion = ref('')
    const questionTimestamp = ref(null)
    const queuePosition = ref(0)
    const estimatedWaitTime = ref(0)
    const assignedOperator = ref(null)
    const lastSeen = ref(null)
    const unreadCount = ref(0)
    const hasAskedQuestion = ref(false)
    const submitting = ref(false)
    const sendingMessage = ref(false)
    const currentChatId = ref(null)
    const chatStartTime = ref(null)
    const chatEndTime = ref(null)
    const operatorClosedChat = ref(false)
    
    // Polling intervals
    let statusPollInterval = null
    let chatPollInterval = null
    let operatorCloseCheckInterval = null
    
    // Department names
    const departmentNames = {
      financial: t('messenger.chatWidget.dept_financial'),
      commercial: t('messenger.chatWidget.dept_commercial'),
      customer: t('messenger.chatWidget.dept_customer'),
      technical: t('messenger.chatWidget.dept_technical'),
      support: t('messenger.chatWidget.dept_support')
    }
    
    // Local Storage Keys
    const STORAGE_KEYS = {
      CHAT_DATA: `chat_${props.department}_${props.companyId}_data`,
      CHAT_STATUS: `chat_${props.department}_${props.companyId}_status`,
      CHAT_MESSAGES: `chat_${props.department}_${props.companyId}_messages`,
      CHAT_ID: `chat_${props.department}_${props.companyId}_id`,
      CHAT_EXPIRY: `chat_${props.department}_${props.companyId}_expiry`,
      OPERATOR_CLOSED: `chat_${props.department}_${props.companyId}_operator_closed`
    }
    
    // Constants
    const CHAT_EXPIRY_TIME = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
    const AUTO_SAVE_INTERVAL = 30000 // Save every 30 seconds
    const OPERATOR_CLOSE_CHECK_INTERVAL = 5000 // Check every 5 seconds
    
    // Auto-save interval
    let autoSaveInterval = null
    
    const getDepartmentName = (dept) => {
      return departmentNames[dept] || dept
    }
    
    // Local Storage Functions
    const saveChatToLocalStorage = () => {
      try {
        if (chatStatus.value === 'ended' || chatStatus.value === 'closed_by_operator') {
          return
        }
        
        const chatData = {
          messages: messages.value,
          chatStatus: chatStatus.value,
          initialQuestion: initialQuestion.value,
          questionTimestamp: questionTimestamp.value,
          hasAskedQuestion: hasAskedQuestion.value,
          assignedOperator: assignedOperator.value,
          lastSeen: lastSeen.value,
          queuePosition: queuePosition.value,
          estimatedWaitTime: estimatedWaitTime.value,
          currentChatId: currentChatId.value,
          chatStartTime: chatStartTime.value,
          chatEndTime: chatEndTime.value,
          companyId: props.companyId,
          timestamp: new Date().getTime(),
          expiry: new Date().getTime() + CHAT_EXPIRY_TIME
        }
        
        localStorage.setItem(STORAGE_KEYS.CHAT_DATA, JSON.stringify(chatData))
        console.log('Chat data saved to local storage')
      } catch (error) {
        console.error('Error saving chat to local storage:', error)
      }
    }
    
    const loadChatFromLocalStorage = () => {
      try {
        const savedData = localStorage.getItem(STORAGE_KEYS.CHAT_DATA)
        
        if (!savedData) {
          return false
        }
        
        const chatData = JSON.parse(savedData)
        const now = new Date().getTime()
        
        // Check if chat data has expired or is for a different company
        if (now > chatData.expiry || chatData.companyId !== props.companyId) {
          clearChatFromLocalStorage()
          return false
        }
        
        // Load data from local storage
        messages.value = chatData.messages || []
        chatStatus.value = chatData.chatStatus
        initialQuestion.value = chatData.initialQuestion || ''
        questionTimestamp.value = chatData.questionTimestamp
        hasAskedQuestion.value = chatData.hasAskedQuestion || false
        assignedOperator.value = chatData.assignedOperator
        lastSeen.value = chatData.lastSeen
        queuePosition.value = chatData.queuePosition || 0
        estimatedWaitTime.value = chatData.estimatedWaitTime || 0
        currentChatId.value = chatData.currentChatId
        chatStartTime.value = chatData.chatStartTime
        chatEndTime.value = chatData.chatEndTime
        
        // Check if operator closed the chat
        const operatorClosed = localStorage.getItem(STORAGE_KEYS.OPERATOR_CLOSED)
        if (operatorClosed === 'true') {
          operatorClosedChat.value = true
          if (chatStatus.value === 'active') {
            chatStatus.value = 'closed_by_operator'
            chatEndTime.value = new Date().toISOString()
          }
        }
        
        // Update unread count based on loaded messages
        if (isMinimized.value && messages.value.length > 0) {
          unreadCount.value = messages.value.filter(m => m.sender === 'operator').length
        }
        
        console.log('Chat data loaded from local storage')
        return true
      } catch (error) {
        console.error('Error loading chat from local storage:', error)
        clearChatFromLocalStorage()
        return false
      }
    }
    
    const clearChatFromLocalStorage = () => {
      try {
        Object.values(STORAGE_KEYS).forEach(key => {
          localStorage.removeItem(key)
        })
        
        const allKeys = Object.keys(localStorage)
        allKeys.forEach(key => {
          if (key.startsWith('chat_') || key.includes('_chat_')) {
            localStorage.removeItem(key)
          }
        })
        
        console.log('Chat data cleared from local storage')
      } catch (error) {
        console.error('Error clearing chat from local storage:', error)
      }
    }
    
    const startAutoSave = () => {
      if (autoSaveInterval) clearInterval(autoSaveInterval)
      
      autoSaveInterval = setInterval(() => {
        if (chatStatus.value === 'active' || chatStatus.value === 'waiting') {
          saveChatToLocalStorage()
        }
      }, AUTO_SAVE_INTERVAL)
    }
    
    const startOperatorCloseCheck = () => {
      if (operatorCloseCheckInterval) clearInterval(operatorCloseCheckInterval)
      
      operatorCloseCheckInterval = setInterval(async () => {
        if (currentChatId.value && chatStatus.value === 'active') {
          await checkIfChatClosedByOperator()
        }
      }, OPERATOR_CLOSE_CHECK_INTERVAL)
    }
    
    const checkIfChatClosedByOperator = async () => {
      try {
        if (!currentChatId.value) return
        
        if (!isAuthenticated.value || !getCustomerId.value) return
        
        const response = await messengerService.getChatStatus(getCustomerId.value)
        const data = response.data || response
        
        if (data.status === 'success') {
          const session = data.session
          
          if (!session) {
            if (chatStatus.value === 'active') {
              operatorClosedChat.value = true
              chatStatus.value = 'closed_by_operator'
              chatEndTime.value = new Date().toISOString()
              
              localStorage.setItem(STORAGE_KEYS.OPERATOR_CLOSED, 'true')
              
              stopAllPolling()
              
              showNotification('گفتگو به پایان رسید', 'اپراتور گفتگو را به پایان رساند.')
              
              console.log('Chat closed by operator detected')
            }
          } else if (session.status === 'ended') {
            operatorClosedChat.value = true
            chatStatus.value = 'closed_by_operator'
            chatEndTime.value = new Date().toISOString()
            
            localStorage.setItem(STORAGE_KEYS.OPERATOR_CLOSED, 'true')
            
            stopAllPolling()
            
            console.log('Chat ended (possibly by operator)')
          }
        }
      } catch (error) {
        console.error('Error checking if chat closed by operator:', error)
      }
    }
    
    const stopAllPolling = () => {
      if (statusPollInterval) {
        clearInterval(statusPollInterval)
        statusPollInterval = null
      }
      if (chatPollInterval) {
        clearInterval(chatPollInterval)
        chatPollInterval = null
      }
      if (operatorCloseCheckInterval) {
        clearInterval(operatorCloseCheckInterval)
        operatorCloseCheckInterval = null
      }
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval)
        autoSaveInterval = null
      }
    }
    
    const initMessenger = async () => {
      try {
        const username = userPhone.value || `customer_${Date.now()}`
        
        console.log('Initializing messenger for:', username)
        const connected = await connectMessenger(username, API_MESSENGER_URL)
        
        if (connected && messengerClient.value) {
          messengerClient.value.onMessageReceived((message) => {
            console.log('Received message via callback:', message)
            processIncomingMessage(message)
          })
          
          console.log('Messenger initialized successfully')
          
          startChatPolling()
        } else {
          console.error('Failed to connect messenger')
        }
      } catch (error) {
        console.error('Failed to initialize messenger:', error)
      }
    }
    
    const startStatusPolling = () => {
      if (statusPollInterval) clearInterval(statusPollInterval)
      
      statusPollInterval = setInterval(async () => {
        await updateChatStatus()
      }, 5000)
    }
    
    const updateChatStatus = async () => {
      if (!isAuthenticated.value || !getCustomerId.value || !chatStatus.value) return
      
      try {
        const response = await messengerService.getChatStatus(getCustomerId.value)
        const data = response.data || response
        
        if (data.status === 'success') {
          const session = data.session
          
          if (session) {
            if (session.status !== chatStatus.value) {
              chatStatus.value = session.status
              
              if (session.status === 'active' && session.chat_id) {
                currentChatId.value = session.chat_id
                chatStartTime.value = new Date().toISOString()
                await loadChatMessages(session.chat_id)
              }
              
              if (session.status === 'ended' && chatStatus.value === 'active') {
                operatorClosedChat.value = true
                chatStatus.value = 'closed_by_operator'
                chatEndTime.value = new Date().toISOString()
                
                localStorage.setItem(STORAGE_KEYS.OPERATOR_CLOSED, 'true')
                
                stopAllPolling()
                
                console.log('Chat ended by operator detected')
              }
            }
            
            if (session.queue_position !== undefined) {
              queuePosition.value = session.queue_position
            }
            
            if (session.assigned_operator && !assignedOperator.value) {
              const operatorId = session.assigned_operator
              
              let operatorFound = false
              
              try {
                const opResponse = await messengerService.findUser(`operator_${operatorId}`)
                const opData = opResponse.data || opResponse
                assignedOperator.value = {
                  id: opData.peer_id,
                  name: opData.username.replace('operator_', ''),
                  department: props.department,
                  lastSeen: opData.last_seen
                }
                lastSeen.value = opData.last_seen
                operatorFound = true
                console.log('Found operator via operator_ prefix:', opData.username)
              } catch (error) {
                console.log('Operator not found with operator_ prefix')
              }
              
              if (!operatorFound) {
                try {
                  const opResponse = await messengerService.findUser(operatorId)
                  const opData = opResponse.data || opResponse
                  assignedOperator.value = {
                    id: opData.peer_id,
                    name: opData.username,
                    department: props.department,
                    lastSeen: opData.last_seen
                  }
                  lastSeen.value = opData.last_seen
                  operatorFound = true
                  console.log('Found operator via direct ID:', opData.username)
                } catch (error) {
                  console.log('Operator not found with direct ID')
                }
              }
              
              if (!operatorFound) {
                console.warn('Could not find operator info, using default')
                assignedOperator.value = {
                  id: `operator_${operatorId}`,
                  name: `اپراتور ${operatorId}`,
                  department: props.department,
                  lastSeen: new Date().toISOString()
                }
              }
            }
            
            if (session.status === 'active') {
              lastSeen.value = new Date().toISOString()
            }
            
            saveChatToLocalStorage()
          } else {
            if (chatStatus.value === 'active') {
              await checkIfChatClosedByOperator()
            } else {
              resetChat()
            }
          }
        }
      } catch (error) {
        console.error('Error updating chat status:', error)
      }
    }
    
    const startChatPolling = () => {
      if (chatPollInterval) clearInterval(chatPollInterval)
      
      chatPollInterval = setInterval(async () => {
        if (chatStatus.value === 'active' && currentChatId.value) {
          await loadChatMessages(currentChatId.value)
        }
      }, 2000)
    }
    
    const submitQuestion = async () => {
      if (!questionText.value.trim() || !isAuthenticated.value || !getCustomerId.value) return
      
      submitting.value = true
      
      try {
        // Pass company ID to the messenger service
        const response = await messengerService.askQuestion(
          getCustomerId.value,
          questionText.value,
          props.department,
          props.companyId  // Add company ID
        )
        const data = response.data || response
        
        console.log('data ', data)
        console.log('data', data.status)

        if (data.status == 'success' || data.status == 'waiting' || data.status == 'active') {
          initialQuestion.value = questionText.value
          questionTimestamp.value = new Date().toISOString()
          chatStartTime.value = new Date().toISOString()
          
          if (data.chat_id) {
            currentChatId.value = data.chat_id
          }
          
          hasAskedQuestion.value = true
          chatStatus.value = data.status
          queuePosition.value = data.queue_position || 0
          estimatedWaitTime.value = data.estimated_wait_time || 0
          
          if (data.status === 'active' && data.chat_id) {
            await loadChatMessages(data.chat_id)
          }
          
          startStatusPolling()
          startAutoSave()
          startOperatorCloseCheck()
          
          saveChatToLocalStorage()
          
          questionText.value = ''
          
          console.log('Question submitted successfully:', data)
        } else {
          throw new Error(data.error || 'Failed to submit question')
        }
      } catch (error) {
        console.error('Error submitting question:', error)
        alert('خطا در ارسال سؤال. لطفاً دوباره تلاش کنید.')
      } finally {
        submitting.value = false
      }
    }
    
    const sendMessage = async () => {
      if (!messageText.value.trim() || !currentChatId.value || !isAuthenticated.value || !getCustomerId.value) return
      
      sendingMessage.value = true
      
      try {
        const messageData = {
          text: messageText.value,
          sender: 'customer',
          timestamp: new Date().toISOString(),
          chat_id: currentChatId.value
        }
        
        messages.value.push(messageData)
        scrollToBottom()
        
        saveChatToLocalStorage()
        
        const response = await messengerService.sendChatMessage(
          currentChatId.value,
          getCustomerId.value,
          messageText.value,
          'customer'
        )
        const data = response.data || response
        
        if (data.status === 'success') {
          console.log('Message sent successfully:', data)
          
          const lastMessage = messages.value[messages.value.length - 1]
          lastMessage.id = data.message_id
          
          saveChatToLocalStorage()
        } else {
          console.error('Failed to send message:', data.error)
          alert('خطا در ارسال پیام')
        }
        
        messageText.value = ''
        
      } catch (error) {
        console.error('Error sending message:', error)
        alert('خطا در ارسال پیام: ' + (error.response?.data?.detail || error.message))
      } finally {
        sendingMessage.value = false
      }
    }
    
    const processIncomingMessage = (message) => {
      console.log('Processing incoming message:', message)
      
      const messageExists = messages.value.some(
        m => m.id === message.id || (m.text === message.text && m.timestamp === message.timestamp)
      )
      
      if (!messageExists) {
        messages.value.push({
          text: message.text || 'پیام جدید',
          sender: 'operator',
          timestamp: message.timestamp || new Date().toISOString(),
          id: message.id
        })
        
        if (isMinimized.value) {
          unreadCount.value++
        }
        
        scrollToBottom()
        
        saveChatToLocalStorage()
        
        showNotification('پیام جدید از اپراتور', message.text || 'پیام جدید دریافت شد')
      }
    }
    
    const endChatFromCustomer = async () => {
      if (!confirm('آیا مطمئن هستید که می‌خواهید گفتگو را به پایان برسانید؟')) {
        return
      }
      
      try {
        if (currentChatId.value) {
          try {
            const response = await messengerService.closeChat(currentChatId.value)
            const data = response.data || response
            if (data.status === 'success') {
              console.log('Chat closed successfully on server')
            } else {
              console.error('Failed to close chat on server:', data.error)
            }
          } catch (error) {
            console.error('Error closing chat on server:', error)
          }
        }
        
        // Clear all chat data
        operatorClosedChat.value = false
        chatStatus.value = 'ended'
        chatEndTime.value = new Date().toISOString()
        currentChatId.value = null
        messages.value = []
        assignedOperator.value = null
        
        // Clear local storage
        localStorage.removeItem(STORAGE_KEYS.OPERATOR_CLOSED)
        clearChatFromLocalStorage()
        
        // Stop all polling
        stopAllPolling()
        
        // Reset hasAskedQuestion to allow new questions
        hasAskedQuestion.value = false
        
        alert('گفتگو با موفقیت به پایان رسید.')
        console.log('Chat ended by customer')
        
      } catch (error) {
        console.error('Error ending chat:', error)
        alert('خطا در پایان گفتگو: ' + (error.response?.data?.detail || error.message))
      }
    }
    
    const saveChatHistory = () => {
      try {
        const chatData = {
          department: props.department,
          departmentName: getDepartmentName(props.department),
          companyId: props.companyId,
          messages: messages.value,
          startTime: chatStartTime.value,
          endTime: chatEndTime.value,
          initialQuestion: initialQuestion.value,
          operator: assignedOperator.value,
          closedByOperator: operatorClosedChat.value
        }
        
        const blob = new Blob([JSON.stringify(chatData, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `chat_history_${props.department}_${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        
        alert('تاریخچه گفتگو با موفقیت ذخیره شد.')
      } catch (error) {
        console.error('Error saving chat history:', error)
        alert('خطا در ذخیره تاریخچه گفتگو.')
      }
    }
    
    const showNotification = (title, body) => {
      if (isMinimized.value && 'Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body: body,
          icon: '/favicon.ico'
        })
      }
    }
    
    const openChat = () => {
      isExpanded.value = true
      isMinimized.value = false
      unreadCount.value = 0
      
      nextTick(() => {
        scrollToBottom()
      })
    }
    
    const minimizeChat = () => {
      isExpanded.value = false
      isMinimized.value = true
      
      saveChatToLocalStorage()
    }
    
    const restoreChat = () => {
      openChat()
    }
    
    const closeChat = () => {
      isExpanded.value = false
      isMinimized.value = false
      
      minimizeChat()
    }
    
    const cancelQuestion = async () => {
      if (confirm('آیا از لغو سؤال اطمینان دارید؟')) {
        resetChat()
      }
    }
    
    const resetChat = () => {
      questionText.value = ''
      messageText.value = ''
      messages.value = []
      chatStatus.value = null
      initialQuestion.value = ''
      questionTimestamp.value = null
      hasAskedQuestion.value = false
      assignedOperator.value = null
      lastSeen.value = null
      queuePosition.value = 0
      estimatedWaitTime.value = 0
      unreadCount.value = 0
      currentChatId.value = null
      chatStartTime.value = null
      chatEndTime.value = null
      operatorClosedChat.value = false
      
      // Stop all polling first
      stopAllPolling()
      
      // Then clear storage
      clearChatFromLocalStorage()
      
      isExpanded.value = true
      isMinimized.value = false
      
      console.log('Chat reset successfully, showing question form')
    }
    
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }
    
    const getStatusText = (status) => {
      const statusMap = {
        waiting: 'در انتظار',
        active: 'فعال',
        ended: 'پایان یافته',
        closed_by_operator: 'بسته شده توسط اپراتور'
      }
      return statusMap[status] || status
    }
    
    const formatTime = (timestamp) => {
      if (!timestamp) return '--:--'
      const date = new Date(timestamp)
      return date.toLocaleTimeString('fa-IR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    }
    
    const formatLastSeen = (timestamp) => {
      const now = new Date()
      const lastSeenDate = new Date(timestamp)
      const diffMinutes = Math.floor((now - lastSeenDate) / (1000 * 60))
      
      if (diffMinutes < 1) return 'آنلاین'
      if (diffMinutes < 60) return `${diffMinutes} دقیقه پیش`
      return `${Math.floor(diffMinutes / 60)} ساعت پیش`
    }
    
    const getInitials = (name) => {
      return name
    }
    
    const requestNotificationPermission = () => {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission()
      }
    }
    
    const initializeChat = async () => {
      const loadedFromStorage = loadChatFromLocalStorage()
      
      if (loadedFromStorage) {
        if (chatStatus.value === 'active') {
          startStatusPolling()
          startChatPolling()
          startOperatorCloseCheck()
          startAutoSave()
        } else if (chatStatus.value === 'waiting') {
          startStatusPolling()
          startAutoSave()
        }
      }
      
      await initMessenger()
    }

    const cleanupImagePreviews = () => {
      messages.value.forEach(message => {
        if (message.type === 'file' && message.previewUrl && message.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(message.previewUrl)
        }
      })
    }

    const handleFileSent = async (fileData) => {
      try {
        if (!isAuthenticated.value || !getCustomerId.value) {
          alert('لطفاً ابتدا وارد سیستم شوید.')
          return
        }
        
        if (!currentChatId.value) {
          alert('لطفاً ابتدا یک گفتگو را شروع کنید.')
          return
        }
        
        const formData = new FormData()
        formData.append('chat_id', currentChatId.value)
        formData.append('sender_id', getCustomerId.value)
        formData.append('sender_type', 'customer')
        formData.append('filename', fileData.file.name)
        formData.append('file_size', fileData.file.size.toString())
        formData.append('file_type', fileData.file.type)
        
        if (fileData.caption) {
          formData.append('caption', fileData.caption)
        }
        
        formData.append('file', fileData.file)
        
        const tempMessage = {
          id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'file',
          fileName: fileData.file.name,
          fileSize: fileData.file.size,
          fileType: fileData.file.type,
          caption: fileData.caption,
          sender: 'customer',
          timestamp: new Date().toISOString(),
          status: 'uploading',
          previewUrl: fileData.file.type.startsWith('image/') ? 
                      URL.createObjectURL(fileData.file) : null
        }
        
        messages.value.push(tempMessage)
        scrollToBottom()
        
        saveChatToLocalStorage()
        
        const response = await fetch(`${API_MESSENGER_URL}/chat/send-file`, {
          method: 'POST',
          body: formData
        })
        
        const result = await response.json()
        
        if (response.ok && result.status === 'success') {
          const messageIndex = messages.value.findIndex(m => m.id === tempMessage.id)
          if (messageIndex !== -1) {
            messages.value[messageIndex] = {
              id: result.message_id,
              type: 'file',
              fileName: result.filename,
              fileSize: result.file_size,
              fileType: result.file_type,
              caption: result.caption,
              sender: 'customer',
              timestamp: result.timestamp,
              status: 'delivered',
              fileId: result.file_id,
              downloadUrl: result.download_url,
              thumbnailUrl: result.thumbnail_url,
              previewUrl: result.thumbnail_url || 
                         (result.file_type.startsWith('image/') ? result.download_url : null)
            }
            
            if (tempMessage.previewUrl && tempMessage.previewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(tempMessage.previewUrl)
            }
            
            console.log('File uploaded successfully:', result)
          }
        } else {
          console.error('File upload failed:', result.error)
          const messageIndex = messages.value.findIndex(m => m.id === tempMessage.id)
          if (messageIndex !== -1) {
            messages.value[messageIndex].status = 'failed'
            messages.value[messageIndex].error = result.error
          }
        }
        
        saveChatToLocalStorage()
        
      } catch (error) {
        console.error('Error handling file upload:', error)
        alert('خطا در ارسال فایل. لطفاً دوباره تلاش کنید.')
      }
    }

    const loadChatMessages = async (chatId) => {
      try {
        const response = await messengerService.getChatMessages(chatId)
        const data = response.data || response
        
        if (data.status === 'success') {
          const formattedMessages = (data.messages || []).map(msg => {
            if (msg.message_type === 'file' || msg.type === 'file') {
              let metadata = {}
              try {
                if (msg.metadata && typeof msg.metadata === 'string') {
                  metadata = JSON.parse(msg.metadata)
                } else if (msg.metadata) {
                  metadata = msg.metadata
                }
              } catch (e) {
                console.error('Error parsing metadata:', e)
              }
              
              const fileMessage = {
                id: msg.id || msg.message_id,
                type: 'file',
                fileName: metadata.filename || msg.filename || msg.file_name,
                fileSize: metadata.file_size || msg.file_size,
                fileType: metadata.file_type || msg.file_type,
                downloadUrl: metadata.download_url || msg.download_url || `/chat/file/${metadata.file_id || msg.file_id}`,
                caption: metadata.caption || msg.caption || msg.message,
                sender: msg.sender_type || msg.sender,
                timestamp: msg.timestamp,
                fileId: metadata.file_id || msg.file_id,
                thumbnailUrl: metadata.thumbnail_url || msg.thumbnail_url
              }
              
              if (isImageFile(fileMessage.fileType)) {
                if (fileMessage.thumbnailUrl) {
                  fileMessage.previewUrl = fileMessage.thumbnailUrl
                } else if (fileMessage.downloadUrl) {
                  fileMessage.previewUrl = fileMessage.downloadUrl
                }
              }
              
              return fileMessage
            } else {
              return {
                id: msg.id || msg.message_id,
                text: msg.message,
                sender: msg.sender_type || msg.sender,
                timestamp: msg.timestamp
              }
            }
          })
          
          formattedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          
          const existingIds = new Set(messages.value.map(m => m.id))
          const newMessages = formattedMessages.filter(msg => 
            msg.id && !existingIds.has(msg.id)
          )
          
          if (newMessages.length > 0) {
            messages.value = [...messages.value, ...newMessages]
            scrollToBottom()
            
            if (isMinimized.value) {
              unreadCount.value += newMessages.filter(m => m.sender === 'operator').length
            }
            
            saveChatToLocalStorage()
            
            console.log('Loaded new messages:', newMessages.length)
          }
        }
      } catch (error) {
        console.error('Error loading chat messages:', error)
      }
    }
    
    // Lifecycle
    onMounted(() => {
      requestNotificationPermission()
      initializeChat()
    })
    
    onUnmounted(() => {
      stopAllPolling()
      disconnectMessenger()
      cleanupImagePreviews()
    })
    
    // Watch for department changes
    watch(() => props.department, () => {
      resetChat()
    })
    
    // Watch for company changes
    watch(() => props.companyId, () => {
      resetChat()
    })
    
    // Watch for state changes and auto-save
    watch(() => [
      messages.value.length,
      chatStatus.value,
      currentChatId.value
    ], () => {
      if (chatStatus.value === 'active' || chatStatus.value === 'waiting') {
        saveChatToLocalStorage()
      }
    }, { deep: true })
    
    return {
      // Refs
      messagesContainer,
      
      // State
      isExpanded,
      isMinimized,
      questionText,
      messageText,
      messages,
      chatStatus,
      initialQuestion,
      questionTimestamp,
      queuePosition,
      estimatedWaitTime,
      assignedOperator,
      lastSeen,
      unreadCount,
      hasAskedQuestion,
      submitting,
      sendingMessage,
      currentChatId,
      chatStartTime,
      chatEndTime,
      operatorClosedChat,
      
      // Computed properties
      isAuthenticated,
      getCustomerId,
      userPhone,
      userName,
      
      // Methods
      getDepartmentName,
      submitQuestion,
      sendMessage,
      openChat,
      minimizeChat,
      restoreChat,
      closeChat,
      cancelQuestion,
      endChatFromCustomer,
      saveChatHistory,
      getStatusText,
      formatTime,
      formatLastSeen,
      getInitials,
      resetChat,

      showFileUploadModal,
      showImagePreview,
      previewImageUrl,
      previewImageName,
      
      // File handling methods
      openFileUploadModal,
      closeFileUploadModal,
      handleFileSent,
      getFileIcon,
      formatFileSize,
      isImageFile,
      openImagePreview,
      closeImagePreview,
      downloadFile,

      getPreviewUrl,
      hasPreview,
      handleImageError,
      cleanupImagePreviews, 
    }
  }
}
</script>

<style scoped>
/* Chat Widget - Theme Friendly */
.chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000000;
  margin: 10px;
}

.chat-toggle-btn {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 20px var(--shadow-2);
  position: relative;
  transition: transform 0.3s;
  margin-bottom: 50px;
  margin-right: 10px;
  
}

.chat-toggle-btn:hover {
  transform: scale(1.1);
}

.unread-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: var(--orange-1);
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-window {
  width: 400px;
  max-width: 90vw;
  height: 600px;
  max-height: 80vh;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 10px 40px var(--shadow-2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--card-border);
}

.chat-header {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dept-icon {
  font-size: 20px;
}

.status-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.status-badge.waiting {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-badge.active {
  background: rgba(76, 175, 80, 0.2);
  color: var(--green);
}

.status-badge.ended, .status-badge.closed_by_operator {
  background: rgba(158, 158, 158, 0.2);
  color: var(--muted);
}

.header-right {
  display: flex;
  gap: 5px;
}

.header-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.close-btn {
  background: rgba(255, 0, 0, 0.3);
}

.close-btn:hover {
  background: rgba(255, 0, 0, 0.5);
}

.chat-messages {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  background: var(--bg);
}

.message {
  max-width: 80%;
  padding: 10px 15px;
  border-radius: 18px;
  position: relative;
}

.message.customer {
  align-self: flex-end;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message.operator {
  align-self: flex-start;
  background: var(--bg);
  color: var(--text);
  border-bottom-left-radius: 4px;
  border: 1px solid var(--card-border);
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.message-text {
  word-break: break-word;
}

.message-time {
  font-size: 11px;
  opacity: 0.8;
}

.message.customer .message-time {
  color: rgba(255, 255, 255, 0.8);
}

.message.operator .message-time {
  color: var(--subtext);
}

.message-sender {
  font-size: 11px;
  margin-top: 5px;
  opacity: 0.7;
}

.operator-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: var(--bg);
  border-radius: 8px;
  margin: 10px 0;
  border: 1px solid var(--card-border);
}

.operator-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.operator-details {
  display: flex;
  flex-direction: column;
}

.operator-name {
  font-weight: 600;
  color: var(--heading);
}

.operator-role {
  font-size: 12px;
  color: var(--subtext);
}

.waiting-section, .question-form {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  background: var(--card-bg);
}

.waiting-icon {
  font-size: 50px;
  margin-bottom: 20px;
  opacity: 0.7;
  color: var(--subtext);
}

.waiting-info {
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--card-border);
  color: var(--text);
}

.cancel-btn, .submit-btn {
  padding: 10px 30px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  margin-top: 20px;
}

.cancel-btn:hover, .submit-btn:hover {
  opacity: 0.9;
}

.cancel-btn:disabled, .submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.message-input {
  padding: 15px;
  border-top: 1px solid var(--card-border);
  display: flex;
  gap: 10px;
  background: var(--card-bg);
}

.message-textarea {
  flex: 1;
  padding: 10px;
  border: 1px solid var(--card-border);
  border-radius: 8px;
  resize: none;
  background: var(--bg);
  color: var(--text);
}

.message-textarea:focus {
  outline: none;
  border-color: var(--primary);
}

.send-btn {
  width: 50px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px 15px;
  margin-top: 30%;
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.char-counter {
  text-align: left;
  width: 100%;
  font-size: 12px;
  color: var(--subtext);
  margin-top: 5px;
}

.chat-footer {
  padding: 10px 15px;
  border-top: 1px solid var(--card-border);
  font-size: 12px;
  color: var(--subtext);
  text-align: center;
  background: var(--card-bg);
}

.minimized-chat {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  padding: 10px 15px;
  border-radius: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: 0 4px 15px var(--shadow-2);
  margin-bottom: 50px;
  margin-right: 10px;
}

.minimized-badge {
  background: var(--orange-1);
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--subtext);
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 15px;
  opacity: 0.5;
  color: var(--muted);
}

@media (max-width: 768px) {
  .chat-window {
    width: 100vw;
    height: 100vh;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }
  
  .chat-widget {
    bottom: 0;
    right: 0;
  }
}

.chat-ended-section {
  padding: 30px 20px;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--card-bg);
}

.ended-icon {
  font-size: 50px;
  margin-bottom: 20px;
  color: var(--green);
}

.chat-summary {
  background: var(--bg);
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  text-align: right;
  width: 100%;
  border: 1px solid var(--card-border);
}

.summary-list {
  list-style: none;
  padding: 0;
  margin: 10px 0 0 0;
}

.summary-list li {
  padding: 5px 0;
  border-bottom: 1px solid var(--card-border);
  color: var(--text);
}

.restart-btn {
  padding: 10px 30px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  margin-top: 10px;
  margin-bottom: 10px;
}

.save-chat-btn {
  padding: 8px 20px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
}

.waiting-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.end-chat-btn {
  padding: 10px 20px;
  background: var(--orange-1);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
}

.end-chat-btn:hover {
  background: var(--orange-2);
}

.end-chat-btn-small {
  padding: 5px 15px;
  background: var(--orange-1);
  color: white;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  font-size: 12px;
  margin-right: auto;
}

.end-chat-btn-empty {
  padding: 8px 20px;
  background: var(--orange-1);
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  margin-top: 15px;
}

.input-actions {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.end-chat-btn-input {
  padding: 8px 15px;
  background: var(--orange-1);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  width: 100%;
}

.end-chat-btn-input:hover {
  background: var(--orange-2);
}

.end-chat-hint {
  color: var(--orange-1);
  font-size: 11px;
}

.minimized-end-btn {
  background: var(--orange-1);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-right: 5px;
}

.minimized-end-btn:hover {
  background: var(--orange-2);
}

/* File Message Styles */
.file-message {
  max-width: 80%;
  padding: 10px;
  border-radius: 18px;
  position: relative;
}

.file-message.customer {
  align-self: flex-end;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  border-bottom-right-radius: 4px;
}

.file-message.operator {
  align-self: flex-start;
  background: var(--bg);
  border-bottom-left-radius: 4px;
  border: 1px solid var(--card-border);
}

.file-message-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.file-icon {
  font-size: 24px;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text);
}

.file-message.customer .file-name {
  color: white;
}

.file-size {
  font-size: 12px;
  color: var(--subtext);
  margin: 2px 0 0 0;
}

.file-message.customer .file-size {
  color: rgba(255, 255, 255, 0.8);
}

.download-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
  padding: 4px;
  color: var(--text);
}

.file-message.customer .download-btn {
  color: white;
}

.download-btn:hover {
  opacity: 1;
}

.file-caption {
  margin: 8px 0;
  padding: 5px 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 14px;
}

.file-message.customer .file-caption {
  background: rgba(255, 255, 255, 0.2);
}

/* File Preview Styles */
.file-preview {
  position: relative;
  margin: 10px 0;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: var(--bg);
  max-width: 300px;
  max-height: 300px;
}

.message.customer .file-preview {
  align-self: flex-end;
}

.message.operator .file-preview {
  align-self: flex-start;
}

.image-preview {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
  display: block;
  transition: transform 0.3s ease;
  border-radius: 8px;
}

.file-preview:hover .image-preview {
  transform: scale(1.02);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 12px;
}

.file-preview:hover .image-overlay {
  opacity: 1;
}

.zoom-icon {
  font-size: 24px;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Image Preview Modal */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000001;
  padding: 20px;
}

.preview-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.full-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 10px 30px var(--shadow-3);
}

.close-preview-btn {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: background 0.3s ease;
}

.close-preview-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.image-info {
  position: absolute;
  bottom: -50px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  padding: 12px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 0 0 12px 12px;
}

.download-full-btn {
  background: var(--primary);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease;
}

.download-full-btn:hover {
  background: var(--primary-hover);
}

/* File upload status */
.file-message.uploading {
  opacity: 0.7;
}

.file-message.failed {
  opacity: 0.5;
}

.file-upload-progress {
  margin-top: 8px;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.file-upload-progress-bar {
  height: 100%;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  transition: width 0.3s ease;
}

/* File Upload Button */
.file-upload-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  color: var(--primary);
  transition: transform 0.2s;
}

.file-upload-btn:hover {
  transform: scale(1.1);
}

.file-upload-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .file-preview {
    max-width: 200px;
    max-height: 200px;
  }
  
  .image-preview {
    max-height: 150px;
  }
  
  .image-preview-modal {
    padding: 10px;
  }
  
  .full-image {
    max-height: 70vh;
  }
  
  .waiting-actions {
    flex-direction: column;
  }
}
</style>